import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { filter, debounceTime, catchError, tap } from 'rxjs/operators';
import { INavData } from '@coreui/angular';

import { environment } from 'src/environments/environment';

export interface HeaderNavItem {
  nav_item_id: string;
  url: string;
  name: string;
  icon: string;
  is_favorite: boolean;
  id?: number;
}

interface NavItemInfo {
  nav_item_id: string;
  url: string;
  name: string;
  icon: string;
  breadcrumb: string;
}

interface TrackEvent {
  nav_item_id: string;
  url: string;
  name: string;
  icon: string;
}

@Injectable({ providedIn: 'root' })
export class HeaderNavService {

  private readonly API_BASE = '/api/accounts/header-nav';
  private readonly appPrefix = environment.prefix;

  private headerNavSubject = new BehaviorSubject<HeaderNavItem[]>([]);
  public headerNav$ = this.headerNavSubject.asObservable();

  private trackBuffer: TrackEvent[] = [];
  private trackFlush$ = new Subject<void>();
  private trackSub: Subscription;
  private routerSub: Subscription;

  /** UUID -> NavItemInfo */
  private navItemByUuid = new Map<string, NavItemInfo>();
  /** URL -> NavItemInfo (para resolver NavigationEnd URL -> UUID) */
  private navItemByUrl = new Map<string, NavItemInfo>();

  private initialized = false;

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  /**
   * Inicializa o serviço. Chamado do BaseLayoutComponent.ngOnInit()
   * após o nav tree estar filtrado por permissões.
   */
  init(navItems: INavData[]): void {
    if (this.initialized) return;
    this.initialized = true;

    this.buildNavItemMaps(navItems);
    this.fetchHeaderNav();

    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.trackNavigation(event.urlAfterRedirects);
    });

    this.trackSub = this.trackFlush$.pipe(
      debounceTime(10000)
    ).subscribe(() => {
      this.flushTrackBuffer();
    });
  }

  destroy(): void {
    this.routerSub?.unsubscribe();
    this.trackSub?.unsubscribe();
    this.flushTrackBuffer();
    this.initialized = false;
  }

  private buildNavItemMaps(items: INavData[], parentPath: string[] = []): void {
    for (const item of items) {
      if (item.url && typeof item.url === 'string' && item.url !== '.') {
        const uuid = item.attributes?.['id'];
        if (uuid) {
          const breadcrumb = [...parentPath, item.name || ''].join(' > ');
          const info: NavItemInfo = {
            nav_item_id: uuid,
            url: item.url,
            name: item.name || '',
            icon: item.icon || '',
            breadcrumb,
          };
          this.navItemByUuid.set(uuid, info);
          this.navItemByUrl.set(item.url, info);
        }
      }
      if (item.children) {
        const childPath = item.name ? [...parentPath, item.name] : parentPath;
        this.buildNavItemMaps(item.children, childPath);
      }
    }
  }

  fetchHeaderNav(): void {
    this.http.get<HeaderNavItem[]>(
      `${this.API_BASE}/?app_prefix=${this.appPrefix}`
    ).pipe(
      catchError(() => {
        return [];
      })
    ).subscribe(items => {
      this.headerNavSubject.next(items || []);
    });
  }

  private trackNavigation(url: string): void {
    const cleanUrl = url.split('?')[0];
    const navItem = this.navItemByUrl.get(cleanUrl);
    if (!navItem) return;

    this.trackBuffer.push({
      nav_item_id: navItem.nav_item_id,
      url: navItem.url,
      name: navItem.name,
      icon: navItem.icon,
    });

    this.trackFlush$.next();
  }

  private flushTrackBuffer(): void {
    if (this.trackBuffer.length === 0) return;

    const events = [...this.trackBuffer];
    this.trackBuffer = [];

    this.http.post(`${this.API_BASE}/track/`, {
      app_prefix: this.appPrefix,
      events,
    }).pipe(
      catchError(() => [])
    ).subscribe();
  }

  addFavorite(navItem: NavItemInfo): Observable<any> {
    return this.http.post(
      `${this.API_BASE}/favorites/?app_prefix=${this.appPrefix}`,
      {
        nav_item_id: navItem.nav_item_id,
        url: navItem.url,
        name: navItem.name,
        icon: navItem.icon,
      }
    ).pipe(
      tap(() => this.fetchHeaderNav())
    );
  }

  removeFavorite(favoriteId: number): Observable<any> {
    return this.http.delete(
      `${this.API_BASE}/favorites/${favoriteId}/`
    ).pipe(
      tap(() => this.fetchHeaderNav())
    );
  }

  removeFavoriteByNavItemId(navItemId: string): void {
    this.http.get<any>(
      `${this.API_BASE}/favorites/?app_prefix=${this.appPrefix}`
    ).subscribe(response => {
      const results = response.results || response;
      const fav = results.find((f: any) => f.nav_item_id === navItemId);
      if (fav) {
        this.removeFavorite(fav.id).subscribe();
      }
    });
  }

  reorderFavorites(orderedIds: number[]): Observable<any> {
    return this.http.post(
      `${this.API_BASE}/favorites/reorder/`,
      { ordered_ids: orderedIds }
    ).pipe(
      tap(() => this.fetchHeaderNav())
    );
  }

  /** Retorna todos os itens navegáveis do sidebar (leaf items com UUID) para o picker de favoritos */
  getAvailableNavItems(): NavItemInfo[] {
    const items: NavItemInfo[] = [];
    this.navItemByUuid.forEach(value => {
      items.push({ ...value });
    });
    return items.sort((a, b) => a.breadcrumb.localeCompare(b.breadcrumb));
  }

  getCurrentFavoriteNavItemIds(): Set<string> {
    const items = this.headerNavSubject.getValue();
    return new Set(items.filter(i => i.is_favorite).map(i => i.nav_item_id));
  }
}
